import { useState, useLayoutEffect, PropsWithChildren } from "react";
import { createPortal } from "react-dom";

interface ReactPortalProps extends PropsWithChildren {
    wrapperId: string;
}

type HTMLElementOrNull = HTMLElement | null;

// a helper function to create an empty div with a given ID, append it to the body, and return the element.
function createWrapperAndAppendToBody(wrapperId: string): HTMLElementOrNull {
    const wrapperElement = document.createElement("div");
    wrapperElement.setAttribute("id", wrapperId);
    document.body.appendChild(wrapperElement);
    return wrapperElement;
}

const ReactPortal = ({ children, wrapperId }: ReactPortalProps) => {
    const [wrapperElement, setWrapperElement] =
        useState<HTMLElementOrNull>(null);
    /**
     * we are directly mutating the DOM and want the effect to run synchronously before the DOM is repainted,
     * that is why we need the useLayoutEffect Hook.
     */
    useLayoutEffect(() => {
        let element: HTMLElementOrNull = document.getElementById(wrapperId);
        if (!element) {
            element = createWrapperAndAppendToBody(wrapperId);
        }
        setWrapperElement(element);

        return () => {
            if (element?.parentNode) {
                element.parentNode.removeChild(element);
            }
        };
    }, [wrapperId]);

    // wrapperElement state will be null on the very first render.
    if (wrapperElement === null) return null;

    return createPortal(children, wrapperElement);
};

export default ReactPortal;
